import { PROCEDURE_IDS, SPECIALTY_IDS } from '../constants';

export const defaultResponse = {
    recommendations: [
        {
            patient_id: 0,
            procedure_id: 76,
            specialty_id: 153,
            is_complete: false,
            title: 'Annual Physical',
            description: 'It\'s recommended that adults get a blood pressure screening (according to the US Preventive Services Task Force), a flu vaccine (according to the CDC), and many more! Discuss with your doctor during an annual physical.',
            month: '2',
            year: '2000',
            cut_off_days: 183,
        },
        {
            patient_id: 0,
            procedure_id: 85,
            specialty_id: 101,
            is_complete: true,
            title: 'Skin Screening',
            description: 'The American Cancer Society recommends an annual skin screening for everyone 20 to 80 years of age.',
            month: '1',
            year: '2018',
            cut_off_days: 365,
        },
        {
            patient_id: 0,
            procedure_id: 11,
            specialty_id: 98,
            is_complete: true,
            title: 'Dental Cleaning',
            description: 'The American Dental Association recommends a routine dental cleaning for everyone 18 and up.',
            month: '7',
            year: '2018',
            cut_off_days: 365,
        },
        {
            patient_id: 0,
            procedure_id: 92,
            specialty_id: 386,
            is_complete: false,
            title: 'Vision Exam',
            description: 'An eye health exam is recommended every two years by the American Academy of Ophthalmology for adults from ages 20 to 80.',
            month: '',
            year: '',
            cut_off_days: 180,
        },
        {
            patient_id: 0,
            procedure_id: 0,
            specialty_id: 0,
            is_complete: true,
            title: 'Sign Up for Zocdoc',
            description: 'Get started by creating a Zocdoc account. There\'s no easier way to get the preventive care you need!',
            month: '',
            year: '',
            cut_off_days: null,
        },
    ],
    saw_well_guide_onboarding: false,
};

const defaultVisionDescription = 'The American Academy of Ophthalmology recommends an eye health exam every 2 years for adults ages 20 to 80.';
const defaultSkinDescription = 'The American Cancer Society recommends an annual skin screening for everyone 20 to 80 years of age.';
const defaultAnnualDescription = 'It\'s recommended that adults get a blood pressure screening (according to the US Preventative Services Task Force), a flu vaccine (according to the CDC), and many more! Discuss with your doctor during your annual physical.';
const defaultDentalDescription = 'The American Dental Association recommends a checkup twice a year. Click, smile, repeat.';
const defaultSignUpDescription = 'Get started by creating a Zocdoc account. There\'s no easier way to get the preventive care you need!';

export const defaultSignUpProcedure = '0';
export const defaultSignUpSpecialty = '0';

const defaultAnnualTitle = 'Annual physical';
const defaultDentalTitle = 'Dental cleaning';
const defaultSkinTitle = 'Skin screening';
const defaultVisionTitle = 'Vision exam';
const defaultSignUpTitle = 'Sign up for Zocdoc';

export const GaActionText = {
    trackClickNewBooking: 'Click New Booking',
};

export const WELL_GUIDE_ROOT_KEY = 'wellGuide';

export const recommendationDescriptions = {
    defaultVisionDescription,
    defaultSkinDescription,
    defaultAnnualDescription,
    defaultDentalDescription,
    defaultSignUpDescription,
};

export const recommendationTitles = {
    defaultVisionTitle,
    defaultSkinTitle,
    defaultAnnualTitle,
    defaultDentalTitle,
    defaultSignUpTitle,
};

export function recommendation(
    procedureId,
    specialtyId,
    title,
    description,
    isComplete,
    month,
    year,
    lastAppointment,
    nextAppointment,
    cutoffDays
) {
    return {
        patient_id: 0,
        procedure_id: procedureId,
        specialty_id: specialtyId,
        is_complete: isComplete,
        title,
        description,
        month,
        year,
        last_appointment: lastAppointment,
        next_appointment: nextAppointment,
        cut_off_days: cutoffDays,
    };
}

const MONTH = '9';
const lastAppointment = year => `Sep ${year}`;
const nextAppointment = year => `May ${year}`;
const CUT_OFF_DAYS = 365;

const createAnnualPhysicalRecommendation =
    (complete = false, currentYear = new Date().getFullYear()) => recommendation(
        PROCEDURE_IDS.ANNUAL_PHYSICAL,
        SPECIALTY_IDS.PRIMARY_CARE_PHYSICIAN,
        defaultAnnualTitle,
        defaultAnnualDescription,
        complete,
        MONTH,
        currentYear.toString(),
        lastAppointment(currentYear),
        nextAppointment(currentYear),
        CUT_OFF_DAYS
    );

const createDentalCleaningRecommendation =
    (complete = false, currentYear = new Date().getFullYear()) => recommendation(
        PROCEDURE_IDS.DENTAL_CLEANING,
        SPECIALTY_IDS.DENTIST,
        defaultDentalTitle,
        defaultDentalDescription,
        complete,
        MONTH,
        currentYear.toString(),
        lastAppointment(currentYear),
        nextAppointment(currentYear),
        CUT_OFF_DAYS
    );

const createSkinScreeningRecommendation =
    (complete = false, currentYear = new Date().getFullYear()) => recommendation(
        PROCEDURE_IDS.ANNUAL_SKIN_SCREENING,
        SPECIALTY_IDS.DERMATOLOGIST,
        defaultSkinTitle,
        defaultSkinDescription,
        complete,
        MONTH,
        currentYear.toString(),
        lastAppointment(currentYear),
        nextAppointment(currentYear),
        CUT_OFF_DAYS
    );

const createVisionExamRecommendation =
    (complete = false, currentYear = new Date().getFullYear()) => recommendation(
        PROCEDURE_IDS.GENERAL_EYE_CONSULTATION,
        SPECIALTY_IDS.EYE_DOCTOR,
        defaultVisionTitle,
        defaultVisionDescription,
        complete,
        MONTH,
        currentYear.toString(),
        lastAppointment(currentYear),
        nextAppointment(currentYear),
        CUT_OFF_DAYS
    );

const createSignUpRecommendation =
    (complete = false, currentYear = new Date().getFullYear()) => recommendation(
        defaultSignUpProcedure,
        defaultSignUpSpecialty,
        defaultSignUpTitle,
        defaultSignUpDescription,
        complete,
        MONTH,
        currentYear.toString(),
        lastAppointment(currentYear),
        nextAppointment(currentYear),
        CUT_OFF_DAYS
    );

export const MOCK_WELL_GUIDE_RECOMMENDATION_FACTORIES = {
    [PROCEDURE_IDS.ANNUAL_PHYSICAL]: createAnnualPhysicalRecommendation,
    [PROCEDURE_IDS.ANNUAL_SKIN_SCREENING]: createSkinScreeningRecommendation,
    [PROCEDURE_IDS.DENTAL_CLEANING]: createDentalCleaningRecommendation,
    [PROCEDURE_IDS.GENERAL_EYE_CONSULTATION]: createVisionExamRecommendation,
    [defaultSignUpProcedure]: createSignUpRecommendation,
};

export function unauthorizedResponse(complete = false) {
    const year = complete ? 2018 : 2017;
    return [
        createVisionExamRecommendation(complete, year),
        createSkinScreeningRecommendation(complete, year),
        createAnnualPhysicalRecommendation(complete, year),
        createDentalCleaningRecommendation(complete, year),
        createSignUpRecommendation(true, 2017),
    ];
}

export const LOCALIZED_STRINGS_MODAL_V3 = {
    DueForVisitText: {
        id: 'WellGuide.Modal.DueForVisitText',
        defaultMessage: 'Due for visit: ',
    },
    LastAppointmentText: {
        id: 'WellGuide.Modal.LastAppoointmentText',
        defaultMessage: '{nextOrLast} appointment: ',
    },
    ProviderSuggestionText: {
        id: 'WellGuide.Modal.DoctoreSuggestions',
        defaultMessage: '{specialty}s you might like in {city}...',
    },
    FindDoctorTextA: {
        id: 'WellGuide.Modal.FindADoctorLink',
        defaultMessage: 'Find a {specialty}',
    },
    FindDoctorTextAn: {
        id: 'WellGuide.Modal.FindADoctorLink',
        defaultMessage: 'Find an {specialty}',
    },
    FindAnotherDoctorText: {
        id: 'WellGuide.Modal.FindADoctorLink',
        defaultMessage: 'Find another {specialty}',
    },
    DentalDescription: {
        id: 'WellGuide.Modal.DentalDescription',
        defaultMessage: defaultDentalDescription,
    },
    PhysicalDescription: {
        id: 'WellGuide.Modal.PhysicalDescription',
        defaultMessage: defaultAnnualDescription,
    },
    SkinDescription: {
        id: 'WellGuide.Modal.SkinDescription',
        defaultMessage: defaultSkinDescription,
    },
    VisionDescription: {
        id: 'WellGuide.Modal.VisionDescription',
        defaultMessage: defaultVisionDescription,
    },
};

export const LOCALIZED_STRING_WELL_GUIDE = {
    OnboardingPrompt: {
        title: {
            defaultMessage: 'Been to the doctor recently?',
            id: 'Booking.Components.WellGuide.OnboardingPrompt.Title',
        },
        description: {
            defaultMessage: 'Complete our Wellness onboarding quiz to see what appointments you may or may not be due for. You might be surprised!',
            id: 'Booking.Components.WellGuide.OnboardingPrompt.Description',
        },
        button: {
            defaultMessage: 'Complete my Well Guide',
            id: 'Booking.Components.WellGuide.OnboardingPrompt.Button',
        },
    },
};

export const PROCEDURE_DESCRIPTIONS = {
    [PROCEDURE_IDS.ANNUAL_PHYSICAL]: LOCALIZED_STRINGS_MODAL_V3.PhysicalDescription,

    [PROCEDURE_IDS.ANNUAL_SKIN_SCREENING]: LOCALIZED_STRINGS_MODAL_V3.SkinDescription,

    [PROCEDURE_IDS.DENTAL_CLEANING]: LOCALIZED_STRINGS_MODAL_V3.DentalDescription,

    [PROCEDURE_IDS.GENERAL_EYE_CONSULTATION]: LOCALIZED_STRINGS_MODAL_V3.VisionDescription,
};
